import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Fuel, AlertTriangle, TrendingUp, TrendingDown, Search, ChevronLeft, ChevronRight, Plus, Filter, X, Eye, Edit, Trash2, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import useStickyState from '../lib/useStickyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClearSkyLogPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useStickyState('clearskylog:page', 1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useStickyState('clearskylog:search', '');
  const [templateId, setTemplateId] = useState(null);
  const [dateFilter, setDateFilter] = useStickyState('clearskylog:dateFilter', 'all');
  const [customDateStart, setCustomDateStart] = useStickyState('clearskylog:customDateStart', '');
  const [customDateEnd, setCustomDateEnd] = useStickyState('clearskylog:customDateEnd', '');
  const [selectedMonth, setSelectedMonth] = useStickyState('clearskylog:selectedMonth', '');
  const [selectedYear, setSelectedYear] = useStickyState('clearskylog:selectedYear', '');
  const perPage = 25;

  useEffect(() => {
    fetchData();
  }, [page]);

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Delete this entry permanently? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.delete(`${API}/forms/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchData();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSafetyReview = async (submissionId) => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.post(
        `${API}/forms/submissions/${submissionId}/safety-review`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.safety_reviewed ? 'Marked as reviewed' : 'Review cleared');
      fetchData();
    } catch (err) {
      console.error('Safety review failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch summary
      const summaryRes = await axios.get(`${API}/clearsky/summary`, { headers });
      setSummary(summaryRes.data);

      // Get the ClearSky template ID first — the submissions endpoint only filters by template_id
      const templatesRes = await axios.get(`${API}/forms/templates`, { headers });
      const clearSkyTemplate = templatesRes.data.find(t => t.name === 'ClearSky Fuel Log');
      const tid = clearSkyTemplate?.id || null;
      if (tid) {
        setTemplateId(tid);
      }

      // Fetch submissions filtered to the ClearSky template only
      if (tid) {
        const res = await axios.get(
          `${API}/forms/submissions?template_id=${tid}&page=${page}&per_page=${perPage}`,
          { headers }
        );
        setEntries(res.data.submissions || []);
        setTotal(res.data.total || 0);
      } else {
        setEntries([]);
        setTotal(0);
      }
    } catch (err) {
      console.error('Failed to fetch ClearSky data:', err);
      toast.error('Failed to load ClearSky log');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Parse YYYY-MM-DD as a LOCAL date so timezone shift doesn't move it back a day.
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString();
    }
    return new Date(dateStr).toLocaleDateString();
  };

  const totalPages = Math.ceil(total / perPage);

  // Generate year options (last 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const clearDateFilter = () => {
    setDateFilter('all');
    setCustomDateStart('');
    setCustomDateEnd('');
    setSelectedMonth('');
    setSelectedYear('');
  };

  // Filter entries by search and date
  const filteredEntries = entries.filter(entry => {
    const data = entry.data || {};
    
    // Text search filter
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesSearch = (
        (entry.submitted_by || '').toLowerCase().includes(searchLower) ||
        (data.unit_number || '').toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }
    
    // Date filter
    if (dateFilter !== 'all') {
      const entryDate = new Date(data.date || entry.submitted_at);
      if (isNaN(entryDate.getTime())) return true;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dateFilter === 'today') {
        const entryDay = new Date(entryDate);
        entryDay.setHours(0, 0, 0, 0);
        return entryDay.getTime() === today.getTime();
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return entryDate >= weekAgo;
      } else if (dateFilter === 'month') {
        if (selectedMonth && selectedYear) {
          const year = parseInt(selectedYear);
          const month = parseInt(selectedMonth) - 1;
          return entryDate.getFullYear() === year && entryDate.getMonth() === month;
        }
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return entryDate >= monthAgo;
      } else if (dateFilter === 'year') {
        if (selectedYear) {
          return entryDate.getFullYear() === parseInt(selectedYear);
        }
        const yearAgo = new Date(today);
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        return entryDate >= yearAgo;
      } else if (dateFilter === 'custom') {
        if (customDateStart && customDateEnd) {
          const start = new Date(customDateStart);
          const end = new Date(customDateEnd);
          end.setHours(23, 59, 59, 999);
          return entryDate >= start && entryDate <= end;
        } else if (customDateStart) {
          return entryDate >= new Date(customDateStart);
        } else if (customDateEnd) {
          const end = new Date(customDateEnd);
          end.setHours(23, 59, 59, 999);
          return entryDate <= end;
        }
      }
    }
    
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/forms">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Forms
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Fuel className="h-6 w-6 text-emerald-500" />
              ClearSky Fuel Log
            </h1>
            <p className="text-muted-foreground text-sm">
              Track diesel fuel and ClearSky additive usage
            </p>
          </div>
        </div>
        {templateId && (
          <Link to={`/dashboard/forms/fill/${templateId}`}>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Entry
            </Button>
          </Link>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-emerald-500/20">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total Entries</div>
              <div className="text-2xl font-bold text-foreground">{summary.total_entries}</div>
            </CardContent>
          </Card>
          <Card className="border-blue-500/20">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total Diesel</div>
              <div className="text-2xl font-bold text-blue-400">
                {summary.totals.diesel_litres.toLocaleString()} L
              </div>
            </CardContent>
          </Card>
          <Card className="border-purple-500/20">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total ClearSky</div>
              <div className="text-2xl font-bold text-purple-400">
                {summary.totals.clearsky_ml.toLocaleString()} mL
              </div>
              <div className="text-xs text-muted-foreground">
                Expected: {summary.totals.expected_clearsky_ml.toLocaleString()} mL
              </div>
            </CardContent>
          </Card>
          <Card className={`${summary.totals.total_variance_ml > 50 ? 'border-yellow-500/50' : 'border-green-500/20'}`}>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total Variance</div>
              <div className={`text-2xl font-bold flex items-center gap-1 ${summary.totals.total_variance_ml > 50 ? 'text-yellow-400' : summary.totals.total_variance_ml < -50 ? 'text-blue-400' : 'text-green-400'}`}>
                {summary.totals.total_variance_ml > 0 ? '+' : ''}{summary.totals.total_variance_ml.toFixed(1)} mL
                {summary.totals.total_variance_ml > 50 && <TrendingUp className="h-5 w-5" />}
                {summary.totals.total_variance_ml < -50 && <TrendingDown className="h-5 w-5" />}
              </div>
              <div className="text-xs text-muted-foreground">
                {summary.totals.variance_percent > 0 ? '+' : ''}{summary.totals.variance_percent}% from expected
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Over-Usage Alert */}
      {summary && summary.over_usage_entries && summary.over_usage_entries.length > 0 && (
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-yellow-400 flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5" />
              Over-Usage Entries ({summary.over_usage_entries.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.over_usage_entries.slice(0, 5).map((entry, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-border/50 last:border-0">
                  <div>
                    <span className="text-foreground font-medium">{entry.driver}</span>
                    <span className="text-muted-foreground"> - Unit {entry.unit}</span>
                    <span className="text-muted-foreground text-xs ml-2">({entry.date})</span>
                  </div>
                  <div className="text-right">
                    <span className="text-yellow-400 font-medium">+{entry.variance.toFixed(1)} mL</span>
                    <span className="text-muted-foreground text-xs ml-2">(+{entry.variance_percent}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Date Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Text Search */}
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by driver or unit..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Date Filter Type */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Date filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">By Month</SelectItem>
                  <SelectItem value="year">By Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Month/Year selectors */}
            {dateFilter === 'month' && (
              <>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'
                    ].map((month, idx) => (
                      <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            {dateFilter === 'year' && (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {dateFilter === 'custom' && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">From:</span>
                  <Input
                    type="date"
                    value={customDateStart}
                    onChange={(e) => setCustomDateStart(e.target.value)}
                    className="w-[150px]"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">To:</span>
                  <Input
                    type="date"
                    value={customDateEnd}
                    onChange={(e) => setCustomDateEnd(e.target.value)}
                    className="w-[150px]"
                  />
                </div>
              </>
            )}

            {dateFilter !== 'all' && (
              <Button variant="ghost" size="sm" onClick={clearDateFilter}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}

            <div className="text-sm text-muted-foreground ml-auto">
              {filteredEntries.length} of {total} entries
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Driver</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Unit</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Diesel (L)</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">ClearSky (mL)</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Expected (mL)</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Variance</th>
                  {isAdminOrManager && (
                    <th className="text-center p-4 text-sm font-medium text-muted-foreground" title="Reviewed by safety person">Reviewed</th>
                  )}
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isAdminOrManager ? 9 : 8} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan={isAdminOrManager ? 9 : 8} className="text-center py-8 text-muted-foreground">
                      No entries found
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((entry) => {
                    const data = entry.data || {};
                    const diesel = parseFloat(data.diesel_added) || 0;
                    const clearsky = parseFloat(data.clearsky_added) || 0;
                    const expected = diesel * 0.25;
                    const variance = clearsky - expected;
                    const isOver = variance > 5;
                    const isUnder = variance < -5;

                    return (
                      <tr key={entry.id} className={`border-b border-border/50 hover:bg-secondary/30 ${isOver ? 'bg-yellow-500/5' : ''}`}>
                        <td className="p-4 text-sm">{formatDate(data.date)}</td>
                        <td className="p-4 text-sm font-medium">{entry.submitted_by}</td>
                        <td className="p-4 text-sm">{data.unit_number}</td>
                        <td className="p-4 text-sm text-right">{diesel.toLocaleString()}</td>
                        <td className="p-4 text-sm text-right">{clearsky.toLocaleString()}</td>
                        <td className="p-4 text-sm text-right text-muted-foreground">{expected.toFixed(1)}</td>
                        <td className={`p-4 text-sm text-right font-medium ${isOver ? 'text-yellow-400' : isUnder ? 'text-blue-400' : 'text-green-400'}`}>
                          {variance > 0 ? '+' : ''}{variance.toFixed(1)}
                          {isOver && <AlertTriangle className="inline h-3 w-3 ml-1" />}
                        </td>
                        {isAdminOrManager && (
                          <td className="p-4 text-center align-middle">
                            <button
                              type="button"
                              onClick={() => handleSafetyReview(entry.id)}
                              className={`inline-flex items-center justify-center h-6 w-6 rounded border-2 transition-colors cursor-pointer ${
                                entry.safety_reviewed
                                  ? 'bg-green-600 border-green-600 text-white'
                                  : 'bg-transparent border-muted-foreground/40 text-transparent hover:border-green-500'
                              }`}
                              data-testid={`clearsky-review-${entry.id}`}
                              title={entry.safety_reviewed ? `Reviewed by ${entry.safety_reviewed_by || ''}` : 'Mark as reviewed'}
                            >
                              <Check className="h-4 w-4" strokeWidth={3} />
                            </button>
                          </td>
                        )}
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Link to={`/dashboard/forms/view/${entry.id}`}>
                              <Button variant="ghost" size="sm" title="View" data-testid={`clearsky-view-${entry.id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Link>
                            <Link to={`/dashboard/forms/edit/${entry.id}`}>
                              <Button variant="ghost" size="sm" title="Edit" data-testid={`clearsky-edit-${entry.id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            </Link>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                onClick={() => handleDeleteSubmission(entry.id)}
                                data-testid={`clearsky-delete-${entry.id}`}
                                title="Delete entry"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* By Unit Summary */}
      {summary && summary.by_unit && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Usage by Unit</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(summary.by_unit).map(([unit, data]) => (
                <div key={unit} className="p-3 rounded-lg bg-secondary/30 border border-border/50">
                  <div className="font-medium text-foreground">Unit {unit}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {data.entries} entries
                  </div>
                  <div className="text-sm text-blue-400 mt-2">
                    {data.diesel.toLocaleString()} L diesel
                  </div>
                  <div className={`text-xs mt-1 ${data.variance > 5 ? 'text-yellow-400' : data.variance < -5 ? 'text-blue-400' : 'text-green-400'}`}>
                    Variance: {data.variance > 0 ? '+' : ''}{data.variance.toFixed(1)} mL
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
